import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Collapse,
  InputAdornment,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { ChevronDown, ChevronRight, Info } from 'lucide-react';
import {
  DEFER_MAX_SECONDS,
  DEFER_MIN_SECONDS,
  DEFER_PREFILL_SECONDS,
  JOB_STATUS_TOOL,
  LONG_RUNNING_COPY as COPY,
  POLL_MAX_SECONDS,
  POLL_MIN_SECONDS,
  RUNTIME_MIN_SECONDS,
  deferLikelyTooSlow,
} from './mcpUtils';

const LABEL_WIDTH = 280;

const InfoTip = ({ title }) => (
  <Tooltip title={title} placement="top" arrow>
    <Box
      component="span"
      tabIndex={0}
      sx={{ display: 'inline-flex', color: 'text.secondary', cursor: 'help' }}
    >
      <Info size={15} />
    </Box>
  </Tooltip>
);

const SecondsField = ({ value, onChange, placeholder, error, min, max, label, disabled }) => (
  <TextField
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    error={error}
    type="number"
    size="small"
    disabled={disabled}
    sx={{ width: 210 }}
    slotProps={{
      input: { endAdornment: <InputAdornment position="end">seconds</InputAdornment> },
      htmlInput: { min, max, step: 1, 'aria-label': label },
    }}
  />
);

const FieldNote = ({ error, children }) => (
  <Typography
    variant="caption"
    sx={{ display: 'block', mt: 0.5, color: error ? 'error.main' : 'text.secondary', lineHeight: 1.45 }}
  >
    {error || children}
  </Typography>
);

/**
 * "Answer slow requests with a job ID" and its two advanced fields — agent
 * servers only. Style-agnostic like McpOAuthFields, so the create dialog can
 * offer the same block without the copy drifting.
 *
 * `value` is the shape `longRunningToForm` returns; every number is kept as
 * the text typed so an empty field stays empty rather than becoming 0.
 */
const McpLongRunningFields = ({ value, errors = {}, onChange, disabled = false }) => {
  const [showAdvanced, setShowAdvanced] = useState(
    !!(value.poll_after_seconds || value.max_runtime_seconds)
  );

  // A rejected save must never point at a field that is collapsed out of view.
  const advancedError = !!(errors.poll_after_seconds || errors.max_runtime_seconds);
  useEffect(() => {
    if (advancedError) setShowAdvanced(true);
  }, [advancedError]);

  // Switching on with nothing (or 0) in the field starts from the value that
  // works for most apps, rather than an empty field that fails validation.
  const handleToggle = (on) => {
    onChange('enabled', on);
    if (on && !Number(value.defer_after_seconds)) {
      onChange('defer_after_seconds', String(DEFER_PREFILL_SECONDS));
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: LABEL_WIDTH }}>
            <Switch
              checked={value.enabled}
              onChange={(e) => handleToggle(e.target.checked)}
              disabled={disabled}
              inputProps={{ 'aria-label': COPY.toggleLabel }}
            />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {COPY.toggleLabel}
            </Typography>
            <InfoTip title={COPY.toggleTooltip} />
          </Box>
          {value.enabled && (
            <SecondsField
              value={value.defer_after_seconds}
              onChange={(v) => onChange('defer_after_seconds', v)}
              placeholder={String(DEFER_PREFILL_SECONDS)}
              error={!!errors.defer_after_seconds}
              min={DEFER_MIN_SECONDS}
              max={DEFER_MAX_SECONDS}
              label={COPY.toggleLabel}
              disabled={disabled}
            />
          )}
        </Box>
        {value.enabled && errors.defer_after_seconds && (
          <FieldNote error={errors.defer_after_seconds} />
        )}
      </Box>

      {/* Advice only — some apps do wait longer than 60 s, so the API accepts it. */}
      {deferLikelyTooSlow(value) && !errors.defer_after_seconds && (
        <Alert severity="warning">{COPY.slowWarning}</Alert>
      )}

      {value.enabled && (
        <>
          <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.55 }}>
            After turning this on, reconnect this server in each app that uses it (Claude, ChatGPT,
            Town), so the app picks up the new <strong>{JOB_STATUS_TOOL}</strong> tool.
          </Typography>

          <Box>
            <Button
              size="small"
              onClick={() => setShowAdvanced((v) => !v)}
              startIcon={showAdvanced ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
              sx={{ textTransform: 'none', color: 'text.secondary', fontWeight: 600 }}
            >
              Advanced (optional)
            </Button>
            <Collapse in={showAdvanced}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1.5, pl: 1 }}>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: LABEL_WIDTH }}>
                      <Typography variant="body2">{COPY.pollLabel}</Typography>
                      <InfoTip title={COPY.pollTooltip} />
                    </Box>
                    <SecondsField
                      value={value.poll_after_seconds}
                      onChange={(v) => onChange('poll_after_seconds', v)}
                      placeholder={COPY.pollPlaceholder}
                      error={!!errors.poll_after_seconds}
                      min={POLL_MIN_SECONDS}
                      max={POLL_MAX_SECONDS}
                      label={COPY.pollLabel}
                      disabled={disabled}
                    />
                  </Box>
                  <FieldNote error={errors.poll_after_seconds}>{COPY.pollHelp}</FieldNote>
                </Box>

                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: LABEL_WIDTH }}>
                      <Typography variant="body2">{COPY.runtimeLabel}</Typography>
                      <InfoTip title={COPY.runtimeTooltip} />
                    </Box>
                    {/* No max: the platform limit comes from server config, and
                        the API's 400 names it. */}
                    <SecondsField
                      value={value.max_runtime_seconds}
                      onChange={(v) => onChange('max_runtime_seconds', v)}
                      placeholder={COPY.runtimePlaceholder}
                      error={!!errors.max_runtime_seconds}
                      min={RUNTIME_MIN_SECONDS}
                      label={COPY.runtimeLabel}
                      disabled={disabled}
                    />
                  </Box>
                  <FieldNote error={errors.max_runtime_seconds}>{COPY.runtimeHelp}</FieldNote>
                </Box>
              </Box>
            </Collapse>
          </Box>
        </>
      )}
    </Box>
  );
};

export default McpLongRunningFields;
